import { Injectable } from '@angular/core';
import { PlessHttp } from 'src/app/shared/http/pless-http.service';
import { PlessRequest } from 'src/app/shared/vo/pless-request.model';
import { PlessJsonResponderSub } from 'src/app/commons/kymw00/pless-json-responder-sub.service';
import { PlessDialogServiceSub } from 'src/app/commons/kymw00/pless-dialog-service-sub';
import {
  KYMW7002Model,
  KYMW7002PricePatternValue,
  KYMW7002OrderUnitType,
  KYMW7002NewUnitType,
} from '../kymw7002.model';
import { SuggestionGoods } from '../vo/suggestion-goods.model';

import type {
  GetSuggestionGoodsInfoCondition,
  GetSuggestionGoodsInfoCombination,
} from '../vo/get-suggestion-goods-info-condition.model';

// リクエスト構造
export interface GetSuggestionGoodsInfoRequest {
  search: {
    SuggestionGoodsInfo: {
      condition: GetSuggestionGoodsInfoCondition;
      combination: {
        condition: GetSuggestionGoodsInfoCombination;
      } [] | null;
    };
  };
}

// レスポンス構造 (実際にはこのオブジェクトの配列で返ってくる)
export interface GetSuggestionGoodsInfoResponse {
  /** Pコード */
  PCode: string;
  /** アイテムコード */
  ItemCode: string;
  /** JANコード */
  JanCode: string;
  /** 商品案内用商品名 */
  GuideGoodsName: string;
  /** 商品案内用規格名 */
  GuideStandardName: string;
  /** メーカー名 */
  MakerName: string;
  /** 価格パターン */
  PricePattern: string;
  /** 供給価格（通常） */
  SupplyPriceStandard: number;
  /** 供給価格（特1） */
  SupplyPriceSpecial1: number;
  /** 供給価格（特2） */
  SupplyPriceSpecial2: number;
  /** 今回供給価格 */
  ThisTimeSupplyPriceStandard: number;
  /** 仕入価格（通常） */
  CostPriceStandard: number;
  /** 仕入価格（特1） */
  CostPriceSpecial1: number;
  /** 仕入価格（特2） */
  CostPriceSpecial2: number;
  /** 今回仕入価格 */
  ThisTimeCostPriceStandard: number;
  /** 在庫数 */
  StockNum: number;
  /** 発注単位 */
  OrderUnitType: string;
  /** 新規区分 */
  NewUnitType: string;
  /** 計画数 */
  PlanNum: number;
  /** 注文番号 */
  OrderNum: string;
  /** 備考 */
  Remarks: string;
}

/**
 * 提案商品情報 取得サービス
 * @export GetSuggestionGoodsInfoService
 * @class GetSuggestionGoodsInfoService
 * @extends {PlessJsonResponderSub}
 */
@Injectable()
export class GetSuggestionGoodsInfoService extends PlessJsonResponderSub<GetSuggestionGoodsInfoResponse[]> {
  model: KYMW7002Model;
  private requestData: GetSuggestionGoodsInfoRequest;
  private calbak: (newSuggestionGoodsList: SuggestionGoods[]) => void;

  constructor(
    protected plessHttp: PlessHttp,
    protected dialogService: PlessDialogServiceSub
  ) {
    super(plessHttp, dialogService);
  }

  /**
   * bodyにセットする値を返す
   *
   * @private
   * @returns {PlessRequest} リクエストJSON
   * @memberof GetSuggestionGoodsInfoService
   */
  private getBody(pcodeList: string[]): PlessRequest {
    // 検索条件を設定
    const condition: GetSuggestionGoodsInfoCondition = {
      OrderCode: this.model.orderCode,
      MediumType: this.model.mediumType,
      FeatureCode: this.model.featureCode,
    };

    // リクエストを設定
    this.requestData = {
      search: {
        SuggestionGoodsInfo: {
          condition,
          combination: null,
        }
      }
    };

    // 検索対象のPコードを配列で渡す
    const combinations = [] as { condition: GetSuggestionGoodsInfoCombination }[];
    for (const pcode of pcodeList) {
      combinations.push({
        condition: { PCode: pcode }
      });
    }
    if (combinations.length > 0) {
      this.requestData.search.SuggestionGoodsInfo.combination = combinations;
    }

    return new PlessRequest(this.requestData);
  }

  /**
   * API呼び出し
   *
   * @param model
   * @memberof GetSuggestionGoodsInfoService
   */
  execute(model: KYMW7002Model, pcodeList: string[], f?: (newSuggestionGoodsList: SuggestionGoods[]) => void): void {
    this.model = model;
    if (f) this.calbak = f;
    this.send('getSuggestionGoodsInfo', this.getBody(pcodeList));
  }

  /**
   * API通信成功時の処理
   *
   * @param {*} data レスポンスデータ
   * @memberof GetSuggestionGoodsInfoService
   */
  success(data: GetSuggestionGoodsInfoResponse[]) {
    const newSuggestionGoodsList = data.map(data => {
      return {
        check: false,
        PCode: data.PCode,
        ItemCode: data.ItemCode,
        JanCode: data.JanCode,
        GuideGoodsName: data.GuideGoodsName,
        GuideStandardName: data.GuideStandardName,
        MakerName: data.MakerName,
        PricePattern: data.PricePattern ?? KYMW7002PricePatternValue.STANDARD,
        SupplyPriceStandard: Number(data.SupplyPriceStandard),
        SupplyPriceSpecial1: Number(data.SupplyPriceSpecial1),
        SupplyPriceSpecial2: Number(data.SupplyPriceSpecial2),
        ThisTimeSupplyPriceStandard: Number(data.ThisTimeSupplyPriceStandard),
        CostPriceStandard: Number(data.CostPriceStandard),
        CostPriceSpecial1: Number(data.CostPriceSpecial1),
        CostPriceSpecial2: Number(data.CostPriceSpecial2),
        ThisTimeCostPriceStandard: Number(data.ThisTimeCostPriceStandard),
        StockNum: data.StockNum ? Number(data.StockNum) : null,
        OrderUnitType: data.OrderUnitType ?? KYMW7002OrderUnitType.INDIVIDUALLY,
        NewUnitType: data.NewUnitType ?? KYMW7002NewUnitType.EXISTING,
        PlanNum: data.PlanNum ? Number(data.PlanNum) : null,
        OrderNum: data.OrderNum,
        Remarks: data.Remarks
      } as SuggestionGoods;
    });

    if (this.calbak) this.calbak(newSuggestionGoodsList);
  }

}
